import Chip from "@mui/material/Chip";
import { styled } from "../../../../lib/styled";
import { useTranslation } from "react-i18next";
import type { ClaimVerdict } from "../../../../types/claims";
import { ConfidenceBar } from "../ConfidenceBar";

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const VERDICT_COLORS: Record<ClaimVerdict, ChipColor> = {
  SUPPORTED: "success",
  LEANING_SUPPORTED: "success",
  LEANING_CONTRADICTED: "error",
  CONTRADICTED: "error",
  MIXED: "secondary",
  INCONCLUSIVE: "default",
  INSUFFICIENT: "default",
  NO_COVERAGE: "default",
  PARTIALLY_SUPPORTED: "warning",
};

interface VerdictBadgeProps {
  verdict: ClaimVerdict;
  /** The evidence direction; omit it, with `conflictDetected`, to show the verdict alone. */
  signal?: number;
  conflictDetected?: boolean;
  /** `compact` for a badge inside a table row or list. */
  size?: "default" | "compact";
}

const VerdictChip = styled(Chip)<{ $compact: boolean }>(({ $compact }) => ({
  fontWeight: 700,
  letterSpacing: 0.5,
  fontSize: $compact ? "0.7rem" : "0.85rem",
}));

export function VerdictBadge({
  verdict,
  signal,
  conflictDetected,
  size = "default",
}: VerdictBadgeProps) {
  const { t } = useTranslation();

  // Inline object ensures all ClaimVerdict values are covered and keys are typed.
  const label: Record<ClaimVerdict, string> = {
    SUPPORTED: t("verdicts.SUPPORTED"),
    LEANING_SUPPORTED: t("verdicts.LEANING_SUPPORTED"),
    LEANING_CONTRADICTED: t("verdicts.LEANING_CONTRADICTED"),
    CONTRADICTED: t("verdicts.CONTRADICTED"),
    MIXED: t("verdicts.MIXED"),
    INCONCLUSIVE: t("verdicts.INCONCLUSIVE"),
    INSUFFICIENT: t("verdicts.INSUFFICIENT"),
    NO_COVERAGE: t("verdicts.NO_COVERAGE"),
    PARTIALLY_SUPPORTED: t("verdicts.PARTIALLY_SUPPORTED"),
  };

  return (
    <div>
      <VerdictChip
        label={label[verdict].toUpperCase()}
        color={VERDICT_COLORS[verdict]}
        size={size === "compact" ? "small" : "medium"}
        $compact={size === "compact"}
      />
      {signal !== undefined && (
        <ConfidenceBar signal={signal} conflictDetected={conflictDetected ?? false} />
      )}
    </div>
  );
}
